import React from 'react';
import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { XCircle, RefreshCw, Zap } from 'lucide-react'
import { scanUrl, getScanResult } from '../services/api'
import ScanForm, { type ScanFormData } from './ScanForm'
import ScanResults from './ScanResults'
import LoadingAnimation from './LoadingAnimation'

export default function InlineScanSection() {
  const [scanId, setScanId] = useState<string | null>(null)
  const [showResults, setShowResults] = useState(false)
  const [normalizedUrl, setNormalizedUrl] = useState<string>('')

  const scanMutation = useMutation({
    mutationFn: scanUrl,
    onSuccess: (data) => {
      setScanId(data.id)
      toast.success('Scan started successfully')
    },
    onError: (error: any) => {
      console.error('Scan error:', error)
      toast.error(error.response?.data?.error?.message || error.message || 'Failed to start scan')
      // Clear any hanging state
      setScanId(null)
      setShowResults(false)
    },
    retry: false,
  })

  const { data: scanResult, isLoading: isPolling, isFetching } = useQuery({
    queryKey: ['scan', scanId],
    queryFn: () => getScanResult(scanId!),
    enabled: !!scanId && !showResults,
    refetchInterval: (data, query) => {
      if (data?.status === 'completed' || data?.status === 'failed') {
        setShowResults(true)
        return false
      }

      // Check timeout: if query has been running for more than 5 minutes
      const queryStartTime = query.state.dataUpdatedAt || Date.now()
      const elapsedTime = Date.now() - queryStartTime

      if (elapsedTime > 300000) { // 5 minutes timeout
        console.log('Query timeout detected, stopping polling')
        toast.error('Test timed out after 5 minutes. This can happen with very complex websites. Please try again.')
        setScanId(null)
        setShowResults(false)
        return false
      }

      return 2000
    },
    retry: (failureCount, error: any) => {
      if (failureCount >= 3) return false

      // Don't retry on 404 errors (scan not found)
      if (error?.response?.status === 404) {
        toast.error('Test not found. Please try starting a new test.')
        setScanId(null)
        setShowResults(false)
        return false
      }

      return true
    },
    onError: (error: any) => {
      console.error('Polling error:', error)
      toast.error('Error while checking test status. Please try again.')
      setScanId(null)
      setShowResults(false)
    }
  })

  const onSubmit = (data: ScanFormData) => {
    console.log('Form submitted with data:', data)
    setNormalizedUrl(data.url)
    setShowResults(false)
    setScanId(null)
    scanMutation.mutate(data)
  }

  // Only consider polling when we actually have a scanId
  const actuallyPolling = !!scanId && (isPolling || isFetching)
  const isScanning = scanMutation.isLoading || actuallyPolling

  // Debug logging
  React.useEffect(() => {
    console.log('InlineScanSection Debug:', {
      isScanning,
      mutationLoading: scanMutation.isLoading,
      queryIsLoading: isPolling,
      queryIsFetching: isFetching,
      actuallyPolling,
      scanId,
      showResults
    })
  }, [isScanning, scanMutation.isLoading, isPolling, isFetching, actuallyPolling, scanId, showResults])

  return (
    <section id="scan-section" className="pb-12">
      <div className="max-w-4xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-8">
          <p className="text-lg text-gray-600">
            Enter any URL below to instantly check its accessibility score and WCAG compliance
          </p>
        </div>

        {/* Scan Form - Always visible */}
        {!showResults && !scanResult && (
          <ScanForm
            onSubmit={onSubmit}
            isLoading={isScanning}
            compact={false}
          />
        )}

        {/* Scan Status / Loading */}
        {scanResult && !showResults && (
          <LoadingAnimation url={normalizedUrl} />
        )}

        {/* Results */}
        {showResults && scanResult && scanResult.status === 'completed' && (
          <div className="space-y-6">
            <ScanResults scanId={scanId!} />

            {/* New Scan Button */}
            <div className="flex justify-center">
              <button
                onClick={() => {
                  setShowResults(false)
                  setScanId(null)
                  setNormalizedUrl('')
                }}
                className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                <Zap className="w-5 h-5 mr-2" />
                Test Another Page
              </button>
            </div>
          </div>
        )}

        {/* Error State */}
        {showResults && scanResult && scanResult.status === 'failed' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-start">
              <XCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3" />
              <div>
                <h3 className="text-sm font-medium text-red-900">
                  Scan Failed
                </h3>
                <p className="text-sm text-red-700 mt-1">
                  The scan could not be completed. Please check the URL and try again.
                </p>
                <button
                  onClick={() => {
                    setShowResults(false)
                    setScanId(null)
                    setNormalizedUrl('')
                  }}
                  className="mt-3 inline-flex items-center text-sm font-medium text-red-700 hover:text-red-800"
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Try Again
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
